import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { FormFieldWrapper } from "@/components/ui/form-field-wrapper";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SuggestionList } from "@/components/ui/suggestion-list";
import { useAppForm } from "@/components/ui/tanstack-form";
import { usePlacesAutocomplete } from "@/hooks/use-places-autocomplete";
import { parseGoogleAddressComponents } from "@/lib/parse-google-address-components";
import {
  getRegistrantDisplayAddress1,
  getRegistrantDisplayAddress2,
  getRegistrantDisplayCity,
  getRegistrantDisplayCompanyName,
  getRegistrantDisplayState,
  getRegistrantDisplayZipcode,
  hasRegistrantDisplayAddress,
} from "@/lib/registrant-display";
import { formatPhoneNumber } from "@/lib/utils";
import {
  AccountBaseSchema,
  type AccountFormData,
  AccountSchema,
} from "@/types/account";
import type { AddressDetails } from "@/types/address";
import type { FormEvent, ReactNode } from "react";
import { useState } from "react";

interface AccountFormProps {
  initialData?: {
    primary_id?: string | null;
    name?: string | null;
    company_name?: string | null;
    primary_account_company_name?: string | null;
    primary_account_address1?: string | null;
    primary_account_address2?: string | null;
    primary_account_city?: string | null;
    primary_account_state?: string | null;
    primary_account_zipcode?: string | null;
    email?: string | null;
    phone?: string | null;
  } & Partial<AddressDetails>;
  onSubmit: (data: AccountFormData) => Promise<void>;
  submitButtonText?: string;
  hideSubmit?: boolean;
  /** Skip the form's own Card when it already sits inside another tile. */
  embedded?: boolean;
  children?: ReactNode;
}

export function AccountForm({
  initialData,
  onSubmit,
  submitButtonText = "Save Changes",
  hideSubmit = false,
  embedded = false,
  children,
}: AccountFormProps) {
  const [placesAutoCompleteInput, setPlacesAutoCompleteInput] = useState("");
  const { suggestions } = usePlacesAutocomplete(placesAutoCompleteInput, {
    includedRegionCodes: ["us"],
    includedPrimaryTypes: ["street_address"],
  });

  // Check if account is secondary (read-only)
  const isSecondaryAccount = Boolean(initialData?.primary_id);

  const form = useAppForm({
    defaultValues: {
      name: initialData?.name ?? "",
      company_name: initialData?.company_name ?? "",
      email: initialData?.email ?? "",
      phone: initialData?.phone ?? "",
      address1: initialData?.address1 ?? "",
      address2: initialData?.address2 ?? "",
      city: initialData?.city ?? "",
      state: initialData?.state ?? "",
      zipcode: initialData?.zipcode ?? "",
    },
    validators: {
      onSubmit: isSecondaryAccount
        ? // For secondary accounts, create a schema without address requirements
          (AccountBaseSchema.omit({
            address1: true,
            address2: true,
            city: true,
            state: true,
            zipcode: true,
          }) as any)
        : (AccountSchema as any),
    },
    onSubmit: async ({ value }) => {
      // Don't include address fields in submission if it's secondary (read-only)
      if (isSecondaryAccount) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { address1, address2, city, state, zipcode, ...submitData } =
          value;
        await onSubmit(submitData as AccountFormData);
      } else {
        await onSubmit(value);
      }
    },
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    await form.handleSubmit();
  };

  const handleSuggestionClick = async (
    suggestion: google.maps.places.AutocompleteSuggestion,
  ) => {
    const place = suggestion.placePrediction?.toPlace();
    if (!place) {
      return;
    }
    await place.fetchFields({
      fields: ["addressComponents"],
    });

    if (!place?.addressComponents) {
      return;
    }

    const { address1, address2, city, state, zipcode } =
      parseGoogleAddressComponents(place.addressComponents);

    form.setFieldValue("address1", address1);
    form.validateField("address1", "change");
    form.setFieldValue("address2", address2);
    form.validateField("address2", "change");
    form.setFieldValue("city", city);
    form.validateField("city", "change");
    form.setFieldValue("state", state);
    form.validateField("state", "change");
    form.setFieldValue("zipcode", zipcode);
    form.validateField("zipcode", "change");
  };

  const submitFooter = hideSubmit ? null : (
    <div className={embedded ? "flex justify-between pt-2" : undefined}>
      <form.Subscribe
        selector={(state) => [state.canSubmit, state.isSubmitting]}
      >
        {([canSubmit, isSubmitting]) => (
          <Button
            className="disabled:opacity-20 disabled:cursor-not-allowed"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {isSubmitting ? "Saving..." : submitButtonText}
          </Button>
        )}
      </form.Subscribe>
    </div>
  );

  const fields = (
    <form.AppForm>
      <form className="space-y-6" onSubmit={handleSubmit}>
        {isSecondaryAccount ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                This account is linked to a primary account. Organization
                address and company name are shown below for reference.
              </p>
            </div>
            {getRegistrantDisplayCompanyName(initialData) && (
              <div className="space-y-2">
                <Label>Primary company name</Label>
                <div className="text-sm">
                  {getRegistrantDisplayCompanyName(initialData)}
                </div>
              </div>
            )}
            {hasRegistrantDisplayAddress(initialData) && (
              <div className="space-y-2">
                <Label>Primary address</Label>
                <div className="text-sm space-y-0.5">
                  {getRegistrantDisplayAddress1(initialData) && (
                    <p>{getRegistrantDisplayAddress1(initialData)}</p>
                  )}
                  {getRegistrantDisplayAddress2(initialData) && (
                    <p>{getRegistrantDisplayAddress2(initialData)}</p>
                  )}
                  <p>
                    {[
                      getRegistrantDisplayCity(initialData),
                      getRegistrantDisplayState(initialData),
                      getRegistrantDisplayZipcode(initialData),
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </p>
                </div>
              </div>
            )}
          </div>
        ) : null}
        <form.AppField name="company_name">
          {(field) => (
            <FormFieldWrapper field={field} label="Company Name">
              <Input
                id="company_name"
                placeholder="Company name"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            </FormFieldWrapper>
          )}
        </form.AppField>
        <form.AppField
          name="name"
          validators={{
            onChange: AccountBaseSchema.shape.name,
          }}
        >
          {(field) => (
            <FormFieldWrapper field={field} label="Name">
              <Input
                id="name"
                placeholder="Full name"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            </FormFieldWrapper>
          )}
        </form.AppField>
        <form.AppField
          name="email"
          validators={{
            onChange: AccountBaseSchema.shape.email,
          }}
        >
          {(field) => (
            <FormFieldWrapper field={field} label="Email">
              <Input
                id="email"
                placeholder="Email address"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            </FormFieldWrapper>
          )}
        </form.AppField>
        <form.AppField
          name="phone"
          validators={{
            onChange: AccountBaseSchema.shape.phone,
          }}
        >
          {(field) => (
            <FormFieldWrapper field={field} label="Phone">
              <Input
                id="phone"
                placeholder="(XXX) XXX-XXXX"
                value={formatPhoneNumber(field.state.value)}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
              />
            </FormFieldWrapper>
          )}
        </form.AppField>
        {children}
        {!isSecondaryAccount && (
          <>
            <form.AppField
              name="address1"
              validators={{
                onChange: AccountBaseSchema.shape.address1,
              }}
            >
              {(field) => (
                <FormFieldWrapper field={field} label="Address Line 1">
                  <SuggestionList
                    closeOnBlur
                    suggestions={suggestions}
                    renderSuggestion={(suggestion) => (
                      <span>{suggestion.placePrediction?.text?.text}</span>
                    )}
                    getSuggestionKey={(suggestion) =>
                      suggestion.placePrediction?.placeId ?? ""
                    }
                    onSuggestionSelect={handleSuggestionClick}
                  >
                    <Input
                      id={field.name}
                      placeholder="Enter your street address"
                      value={field.state.value}
                      onChange={(e) => {
                        field.handleChange(e.target.value);
                        setPlacesAutoCompleteInput(e.target.value);
                      }}
                      onBlur={field.handleBlur}
                    />
                  </SuggestionList>
                </FormFieldWrapper>
              )}
            </form.AppField>
            <form.AppField
              name="address2"
              validators={{
                onChange: AccountBaseSchema.shape.address2,
              }}
            >
              {(field) => (
                <FormFieldWrapper
                  field={field}
                  label="Address Line 2 (Optional)"
                >
                  <Input
                    id="address2"
                    placeholder="Apartment, suite, unit, etc."
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                  />
                </FormFieldWrapper>
              )}
            </form.AppField>
            <div className="grid md:grid-cols-3 gap-4 items-start">
              <form.AppField
                name="city"
                validators={{
                  onChange: AccountBaseSchema.shape.city,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="City">
                    <Input
                      id="city"
                      placeholder="Enter your city"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
              <form.AppField
                name="state"
                validators={{
                  onChange: AccountBaseSchema.shape.state,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="State">
                    <Input
                      id="state"
                      placeholder="XX"
                      maxLength={2}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
              <form.AppField
                name="zipcode"
                validators={{
                  onChange: AccountBaseSchema.shape.zipcode,
                }}
              >
                {(field) => (
                  <FormFieldWrapper field={field} label="ZIP Code">
                    <Input
                      id="zipcode"
                      placeholder="XXXXX or XXXXX-XXXX"
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                    />
                  </FormFieldWrapper>
                )}
              </form.AppField>
            </div>
          </>
        )}
      </form>
    </form.AppForm>
  );

  if (embedded) {
    return (
      <div className="space-y-4">
        {fields}
        {submitFooter}
      </div>
    );
  }

  return (
    <Card>
      <CardContent>{fields}</CardContent>
      {hideSubmit ? null : (
        <CardFooter className="flex justify-between">{submitFooter}</CardFooter>
      )}
    </Card>
  );
}
